import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Copy, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";
import { http, money, errMsg } from "../lib/api";
import { FareBreakdown } from "../components/FareBreakdown";

const CardPay = ({ amount, bookingId }) => (
  <div data-testid="card-pay">
    <div className="font-heading font-bold mb-1">Step 1 · Pay {money(amount)} with your credit card</div>
    <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1 mt-2">
      <li>Open Google Pay, PhonePe, Paytm or BHIM with your <b>RuPay credit card</b> linked to UPI.</li>
      <li>Scan this QR code (or save it and scan it from your gallery).</li>
      <li>Enter exactly <b className="font-mono">{money(amount)}</b>, choose your credit card and pay. Add {bookingId} in the note.</li>
    </ol>
    <div className="mt-4 flex flex-col items-center gap-3">
      <img src="/wn-merchant-qr.jpg" alt="WanderNest Tour and Travel merchant QR" className="w-auto max-h-[360px] max-w-full rounded-xl border" data-testid="merchant-qr-img" />
      <a href="/wn-merchant-qr.jpg" download="WanderNest-QR.jpg" data-testid="merchant-qr-download" className="rounded-full border px-4 py-2 text-sm font-semibold">Save QR to phone</a>
    </div>
    <p className="text-xs text-slate-500 mt-3">Have a Visa or Mastercard card? WhatsApp us and we'll help you pay.</p>
  </div>
);

export default function Pay() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { config } = useAuth();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("upi");

  useEffect(() => { http.get(`/bookings/${bookingId}`).then((r) => setB(r.data)).catch((e) => setError(errMsg(e))); }, [bookingId]);

  const amount = b?.prices?.total || 0;
  const upiUrl = `upi://pay?${new URLSearchParams({ pa: config.upi_id || "", pn: config.payee_name || "WanderNest", am: amount.toFixed(2), cu: "INR", tn: `WanderNest ${bookingId}` }).toString()}`;
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const submit = async () => {
    setBusy(true);
    try {
      await http.post(`/bookings/${bookingId}/utr`, { utr });
      toast.success("Payment details submitted. We'll verify and issue your ticket soon.");
      navigate(`/booking/${bookingId}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const canPay = b && ["pending_payment", "payment_rejected"].includes(b.status);

  return (
    <div className="min-h-screen bg-[#F4F6F9] pb-16">
      <Header />
      <div className="mx-auto max-w-xl px-4 py-8">
        {error && <div className="bg-white rounded-2xl p-10 text-center" data-testid="pay-error">{error}</div>}
        {!b && !error && <Skeleton className="h-96 rounded-2xl" />}
        {b && !canPay && (
          <div className="bg-white rounded-2xl p-8 text-center" data-testid="pay-already-done">
            <CheckCircle2 className="h-10 w-10 mx-auto text-[#0F9D58]" />
            <p className="mt-3 font-semibold">Payment already submitted for this booking.</p>
            <Link to={`/booking/${bookingId}`} className="inline-block mt-4 text-[#EC5B24] font-semibold">View booking</Link>
          </div>
        )}
        {canPay && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-rise" data-testid="upi-pay-card">
            <div className="bg-[#1C1A4A] text-white p-6">
              <div className="text-sm text-white/70">Booking {b.booking_id}</div>
              <div className="font-mono text-4xl font-extrabold mt-1" data-testid="pay-amount">{money(amount)}</div>
              <div className="text-sm text-white/70 mt-1" data-testid="pay-route">{b.offer.slices.length > 1 ? `Round trip · ${b.offer.slices[0].origin} ⇄ ${b.offer.slices[0].destination}` : `${b.offer.slices[0].origin} → ${b.offer.slices[0].destination}`} · {b.travellers.length} traveller(s)</div>
            </div>
            <FareBreakdown b={b} />
            {b.status === "payment_rejected" && (
              <div className="flex gap-2 bg-red-50 text-red-600 text-sm px-6 py-3" data-testid="payment-rejected-note">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> We couldn't find your last payment{b.payment?.reject_reason ? `: ${b.payment.reject_reason}` : ""}. Please pay again or recheck the reference number.
              </div>
            )}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1" data-testid="pay-method-tabs">
                {[["upi", "UPI app / QR"], ["card", "Credit card"]].map(([k, l]) => (
                  <button key={k} data-testid={`pay-method-${k}`} onClick={() => setMethod(k)} className={`rounded-lg py-2 text-sm font-semibold transition-colors ${method === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
              {method === "card" ? <CardPay amount={amount} bookingId={b.booking_id} /> : <div>
                <div className="font-heading font-bold mb-1">Step 1 · Pay {money(amount)} by UPI</div>
                <a href={upiUrl} data-testid="open-upi-app-btn"
                  className="mt-3 w-full rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] text-white font-heading font-bold text-lg py-3 flex items-center justify-center gap-2 transition-colors">
                  <Smartphone className="h-5 w-5" /> Pay with UPI app
                </a>
                <p className="text-xs text-slate-500 mt-2 text-center">Opens Google Pay, PhonePe, Paytm or BHIM on your phone</p>
                <div className="flex flex-col sm:flex-row items-center gap-5 mt-5 rounded-xl border border-dashed border-slate-300 p-4">
                  <div className="bg-white p-2 rounded-lg border" data-testid="upi-qr"><QRCodeSVG value={upiUrl} size={140} /></div>
                  <div className="text-sm text-center sm:text-left">
                    <div className="text-slate-500">On a computer? Scan with any UPI app, or pay to</div>
                    <button onClick={() => copy(config.upi_id)} data-testid="copy-upi-id-btn" className="mt-2 inline-flex items-center gap-2 font-mono font-bold text-slate-900 bg-slate-100 rounded-lg px-3 py-1.5">
                      {config.upi_id} <Copy className="h-3.5 w-3.5" />
                    </button>
                    <div className="text-xs text-slate-500 mt-2">Payee: {config.payee_name} · Note: {b.booking_id}</div>
                  </div>
                </div>
              </div>}
              <div>
                <div className="font-heading font-bold mb-1">Step 2 · Enter the UPI transaction ID</div>
                <p className="text-xs text-slate-500 mb-3">You'll find the 12-digit UTR / reference number in your UPI app after paying.</p>
                <input data-testid="utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\s/g, ""))} placeholder="e.g. 412345678901" maxLength={40}
                  className="w-full rounded-xl border border-slate-200 px-3 py-3 font-mono outline-none focus:border-[#EC5B24]" />
                <button data-testid="submit-utr-btn" onClick={submit} disabled={busy || utr.length < 6}
                  className="mt-3 w-full rounded-xl bg-[#1C1A4A] disabled:opacity-50 text-white font-bold py-3 flex items-center justify-center gap-2">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} I have paid · Submit
                </button>
              </div>
              <p className="text-xs text-slate-500">After we confirm the payment, we book your ticket with the airline and share the PNR in My Trips and by email. If the fare changes before we book, we'll contact you first.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
